import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  // 20 s hard cap. Any endpoint that legitimately takes longer (SSE stream,
  // large uploads) should use fetch() directly, not this axios instance.
  timeout: 20000,
});

/**
 * 401 auto-refresh interceptor.
 *
 * When any request returns 401 (except /auth/me, /auth/refresh, /auth/login,
 * /auth/register), we try ONCE to hit /auth/refresh which rotates the
 * cookie pair using the refresh_token. On success we retry the original
 * request. On failure we broadcast a `summit:session-expired` event so
 * AuthContext can bounce the user to /login with a toast.
 *
 * Callers may set `config._silentAuthCheck = true` to opt out of the
 * session-expired broadcast on refresh failure. This is used by the
 * initial /auth/me probe at page load — a 401 there means "anonymous
 * visitor", not "session expired", so we must not toast.
 *
 * A single in-flight refresh Promise is shared across concurrent 401s so
 * we don't stampede /auth/refresh.
 */
let refreshInflight = null;
// Paths that must NEVER trigger a refresh retry:
//   - /auth/refresh itself (obvious loop)
//   - /auth/login and /auth/register (a 401 here is a real credential
//     failure, not an expired session — refreshing would eat the toast)
// `/auth/me` is intentionally NOT in this list — its 401 IS the exact
// signal that the access-token expired at page load and we need to refresh.
// The `original._retried` guard prevents any loop.
const NO_REFRESH_PATHS = ["/auth/refresh", "/auth/login", "/auth/register"];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const url = original.url || "";
    const status = error.response?.status;
    const skip = NO_REFRESH_PATHS.some((p) => url.includes(p));

    if (status !== 401 || original._retried || skip) {
      return Promise.reject(error);
    }
    original._retried = true;

    try {
      refreshInflight = refreshInflight || api.post("/auth/refresh");
      await refreshInflight;
      return api(original);
    } catch (e) {
      // Refresh failed → user is genuinely logged out. Tell the app —
      // unless the caller flagged this as a silent auth-check (initial
      // page-load probe by anonymous visitor), in which case a 401 just
      // means "not signed in" and should NOT trigger a session-expired
      // toast that may race with a subsequent successful login.
      if (!original._silentAuthCheck) {
        const detail = e?.response?.data?.detail;
        window.dispatchEvent(new CustomEvent("summit:session-expired", { detail }));
      }
      return Promise.reject(error);
    } finally {
      refreshInflight = null;
    }
  },
);

export default api;

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
